// src/cockpit4/components/CozeWorkflowPanel.tsx
// ============================================
// 扣子(Coze)工作流集成面板
//
// 集成到工作流标签页，提供：
//   1. 扣子 Bot 管理列表
//   2. 快速对话测试
//   3. 工作流执行
//   4. API Key 配置入口
// ============================================

import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import {
  Bot, Plus, Trash2, Play, Loader2, Settings as SettingsIcon,
  MessageSquare, Workflow as WorkflowIcon, CheckCircle2,
  XCircle, KeyRound, ExternalLink, Sparkles, Zap, AlertTriangle,
} from "lucide-react";
import { cozeIntegration, type CozeBot } from "../../services/cozeIntegration";

// ============================================
// 主组件
// ============================================

export default function CozeWorkflowPanel() {
  const [bots, setBots] = useState<CozeBot[]>([]);
  const [configured, setConfigured] = useState(false);
  const [showConfig, setShowConfig] = useState(false);
  const [showAddBot, setShowAddBot] = useState(false);
  const [selectedBot, setSelectedBot] = useState<CozeBot | null>(null);
  const [chatInput, setChatInput] = useState("");
  const [chatResult, setChatResult] = useState<string | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  const refresh = useCallback(() => {
    setBots(cozeIntegration.getBots());
    setConfigured(cozeIntegration.isConfigured());
  }, []);

  useEffect(() => {
    refresh();
    const unsub = cozeIntegration.subscribe(refresh);
    return unsub;
  }, [refresh]);

  const handleChat = async () => {
    if (!selectedBot || !chatInput.trim()) return;
    setChatLoading(true);
    setChatError(null);
    setChatResult(null);
    try {
      const result = await cozeIntegration.quickChat(selectedBot.bot_id, chatInput);
      setChatResult(result);
    } catch (err) {
      setChatError(err instanceof Error ? err.message : String(err));
    } finally {
      setChatLoading(false);
    }
  };

  const categories = [
    { id: "chat", label: "对话 Bot", icon: MessageSquare, color: "#3B82F6" },
    { id: "workflow", label: "工作流", icon: WorkflowIcon, color: "#A855F7" },
    { id: "agent", label: "智能体", icon: Bot, color: "#22D3EE" },
    { id: "plugin", label: "插件", icon: Zap, color: "#F59E0B" },
  ];

  return (
    <div className="h-full w-full overflow-y-auto p-4">
      <div className="space-y-4 max-w-4xl mx-auto">
        {/* ============ 标题栏 ============ */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-500 flex items-center justify-center">
              <Sparkles size={16} className="text-white" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-white">扣子(Coze) 工作流集成</h3>
              <p className="text-[10px] font-mono text-gray-500 uppercase tracking-wider">
                连接扣子 Bot / 工作流 / 智能体 · 扩展 AI 执行能力
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <a
              href="https://www.coze.cn"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-mono text-gray-500 hover:text-gray-300 border border-white/[0.06] hover:border-white/[0.1] transition-all"
            >
              <ExternalLink size={10} /> coze.cn
            </a>
            <button
              onClick={() => setShowConfig(!showConfig)}
              className={`flex items-center gap-1 px-2.5 py-1 rounded-md text-[10px] font-mono transition-all ${
                configured
                  ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/20"
                  : "bg-amber-500/10 text-amber-300 border border-amber-500/20"
              }`}
            >
              <KeyRound size={10} />
              {configured ? "已连接" : "未配置"}
            </button>
          </div>
        </div>

        {/* ============ 配置面板（可折叠） ============ */}
        {showConfig && (
          <ConfigPanel onClose={() => setShowConfig(false)} />
        )}

        {/* ============ 接入路径指引（4 步） ============ */}
        <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3">
          <div className="flex items-center gap-1.5 mb-2">
            <Sparkles size={12} className="text-teal-400" />
            <span className="text-[11px] font-semibold text-white">接入路径</span>
            <span className="text-[9px] font-mono text-gray-500 ml-auto">
              扣子第三方 API 开放前的落地方式
            </span>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {([
              { title: "配置密钥", desc: "填写 API Key (PAT) 与 Base URL" },
              { title: "平台创建", desc: "在 coze.cn 创建 Bot / 工作流 / 智能体" },
              { title: "手动测试", desc: "本面板快速对话，验证执行结果" },
              { title: "自动同步", desc: "API 开放后自动并入工作流控制台" },
            ] as const).map((step, idx) => (
              <div
                key={step.title}
                className="rounded-md border border-white/[0.04] bg-white/[0.01] px-2 py-1.5"
              >
                <div className="flex items-center gap-1.5 mb-1">
                  <span className="w-4 h-4 rounded-full bg-teal-500/15 border border-teal-500/30 flex items-center justify-center text-[9px] font-mono text-teal-300 flex-shrink-0">
                    {idx + 1}
                  </span>
                  <span className="text-[10px] font-semibold text-gray-200 truncate">{step.title}</span>
                </div>
                <div className="text-[9px] text-gray-500 leading-tight">{step.desc}</div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[9px] font-mono text-amber-400/80 flex items-start gap-1">
            <AlertTriangle size={10} className="mt-0.5 flex-shrink-0" />
            当前为配置预留：扣子暂不支持第三方平台直接调用其 Bot/工作流；API 开放后将自动同步到经营看板工作流控制台。
          </div>
        </div>

        {/* ============ 未配置提示 ============ */}
        {!configured && !showConfig && (
          <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 px-4 py-6 text-center">
            <KeyRound size={24} className="inline text-amber-400 mb-2" />
            <p className="text-[12px] text-amber-300 mb-1">扣子 API 未配置</p>
            <p className="text-[10px] text-gray-500 mb-3">
              配置扣子 API Key 后，可将扣子 Bot/工作流接入 XEAOS 工作流系统
            </p>
            <button
              onClick={() => setShowConfig(true)}
              className="px-3 py-1.5 rounded-md bg-amber-500/15 border border-amber-500/30 text-[11px] font-mono text-amber-300 hover:bg-amber-500/25 transition-all"
            >
              <KeyRound size={11} className="inline mr-1" /> 配置 API Key
            </button>
          </div>
        )}

        {/* ============ 添加 Bot 按钮 ============ */}
        {configured && (
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-mono text-gray-500">
              已连接 {bots.length} 个 Bot · 总调用 {bots.reduce((sum, b) => sum + b.callCount, 0)} 次
            </div>
            <button
              onClick={() => setShowAddBot(!showAddBot)}
              className="flex items-center gap-1 px-2.5 py-1 rounded-md bg-blue-500/10 border border-blue-500/20 text-[10px] font-mono text-blue-300 hover:bg-blue-500/20 transition-all"
            >
              <Plus size={11} /> 添加 Bot
            </button>
          </div>
        )}

        {/* ============ 添加 Bot 表单 ============ */}
        {showAddBot && configured && (
          <AddBotForm
            onAdd={(bot) => {
              cozeIntegration.addBot(bot);
              setShowAddBot(false);
            }}
            onCancel={() => setShowAddBot(false)}
          />
        )}

        {/* ============ Bot 分类列表 ============ */}
        {configured && bots.length > 0 && (
          <div className="space-y-3">
            {categories.map((cat) => {
              const catBots = bots.filter((b) => b.category === cat.id);
              if (catBots.length === 0) return null;
              const CatIcon = cat.icon;
              return (
                <div key={cat.id}>
                  <div className="flex items-center gap-1.5 mb-1.5 px-1">
                    <CatIcon size={11} style={{ color: cat.color }} />
                    <span className="text-[10px] font-mono uppercase tracking-wider text-gray-500">
                      {cat.label} ({catBots.length})
                    </span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {catBots.map((bot) => (
                      <BotCard
                        key={bot.bot_id}
                        bot={bot}
                        selected={selectedBot?.bot_id === bot.bot_id}
                        onSelect={() => setSelectedBot(bot)}
                        onRemove={() => {
                          cozeIntegration.removeBot(bot.bot_id);
                          if (selectedBot?.bot_id === bot.bot_id) setSelectedBot(null);
                        }}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ============ 对话测试区 ============ */}
        {selectedBot && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3"
          >
            <div className="flex items-center gap-1.5 mb-2">
              <MessageSquare size={12} className="text-blue-400" />
              <span className="text-[11px] font-semibold text-white">
                测试对话：{selectedBot.name}
              </span>
            </div>
            <div className="flex gap-2">
              <input
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleChat()}
                placeholder="输入测试消息..."
                className="flex-1 px-3 py-1.5 rounded-md bg-black/30 border border-white/[0.08] text-[12px] text-white outline-none focus:border-blue-500/30"
              />
              <button
                onClick={handleChat}
                disabled={chatLoading || !chatInput.trim()}
                className="flex items-center gap-1 px-3 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/30 text-[11px] font-mono text-blue-300 hover:bg-blue-500/25 transition-all disabled:opacity-50"
              >
                {chatLoading ? <Loader2 size={11} className="animate-spin" /> : <Play size={11} />}
                执行
              </button>
            </div>
            {chatError && (
              <div className="mt-2 px-3 py-2 rounded-md bg-red-500/10 border border-red-500/20 text-[11px] text-red-300">
                <XCircle size={11} className="inline mr-1" /> {chatError}
              </div>
            )}
            {chatResult && (
              <div className="mt-2 px-3 py-2 rounded-md bg-emerald-500/10 border border-emerald-500/20 text-[11px] text-emerald-300">
                <CheckCircle2 size={11} className="inline mr-1" />
                <pre className="whitespace-pre-wrap font-mono mt-1 text-gray-300">{chatResult}</pre>
              </div>
            )}
          </motion.div>
        )}

        {/* ============ 空状态 ============ */}
        {configured && bots.length === 0 && !showAddBot && (
          <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-4 py-8 text-center text-[11px] text-gray-500">
            <Bot size={18} className="inline mr-1 -mt-0.5 text-gray-600" />
            暂无已连接的扣子 Bot。点击「添加 Bot」开始集成。
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// 配置面板
// ============================================

function ConfigPanel({ onClose }: { onClose: () => void }) {
  const [apiKey, setApiKey] = useState(cozeIntegration.getApiKey());
  const [baseUrl, setBaseUrl] = useState(cozeIntegration.getBaseUrl());

  const handleSave = () => {
    cozeIntegration.setApiKey(apiKey, baseUrl);
    onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      exit={{ opacity: 0, height: 0 }}
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2"
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-white">扣子 API 配置</span>
        <button onClick={onClose} className="text-gray-500 hover:text-gray-300 text-[11px]">收起</button>
      </div>
      <label className="block">
        <span className="text-[9px] font-mono text-gray-500">API Key (PAT)</span>
        <input
          type="password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="pat-xxxxxxxxxxxxxxxx"
          className="w-full mt-0.5 px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none"
        />
      </label>
      <label className="block">
        <span className="text-[9px] font-mono text-gray-500">API Base URL</span>
        <input
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value)}
          placeholder="https://api.coze.cn"
          className="w-full mt-0.5 px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none"
        />
      </label>
      <div className="text-[9px] text-gray-600">
        获取 API Key：访问 coze.cn → 个人中心 → API Keys → 创建 Personal Access Token
      </div>
      <button
        onClick={handleSave}
        className="w-full py-1.5 rounded-md bg-blue-500/15 border border-blue-500/30 text-[11px] font-mono text-blue-300 hover:bg-blue-500/25 transition-all"
      >
        保存配置
      </button>
    </motion.div>
  );
}

// ============================================
// 添加 Bot 表单
// ============================================

function AddBotForm({ onAdd, onCancel }: {
  onAdd: (bot: Omit<CozeBot, "connectedAt" | "callCount">) => void;
  onCancel: () => void;
}) {
  const [botId, setBotId] = useState("");
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<CozeBot["category"]>("chat");

  const handleSubmit = () => {
    if (!botId.trim() || !name.trim()) return;
    onAdd({
      bot_id: botId.trim(),
      name: name.trim(),
      description: description.trim() || "未设置描述",
      category,
      status: "active",
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0, height: 0 }}
      animate={{ opacity: 1, height: "auto" }}
      className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-3 space-y-2"
    >
      <div className="grid grid-cols-2 gap-2">
        <label className="block">
          <span className="text-[9px] font-mono text-gray-500">Bot ID</span>
          <input
            value={botId}
            onChange={(e) => setBotId(e.target.value)}
            placeholder="从扣子平台复制 Bot ID"
            className="w-full mt-0.5 px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none"
          />
        </label>
        <label className="block">
          <span className="text-[9px] font-mono text-gray-500">名称</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="给 Bot 起个名字"
            className="w-full mt-0.5 px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none"
          />
        </label>
      </div>
      <label className="block">
        <span className="text-[9px] font-mono text-gray-500">描述</span>
        <input
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Bot 用途说明"
          className="w-full mt-0.5 px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none"
        />
      </label>
      <label className="block">
        <span className="text-[9px] font-mono text-gray-500">类型</span>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value as CozeBot["category"])}
          className="w-full mt-0.5 px-2 py-1.5 rounded bg-black/30 border border-white/[0.08] text-[11px] text-white outline-none"
        >
          <option value="chat">对话 Bot</option>
          <option value="workflow">工作流</option>
          <option value="agent">智能体</option>
          <option value="plugin">插件</option>
        </select>
      </label>
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={!botId.trim() || !name.trim()}
          className="flex-1 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/30 text-[11px] font-mono text-blue-300 hover:bg-blue-500/25 transition-all disabled:opacity-50"
        >
          添加
        </button>
        <button
          onClick={onCancel}
          className="px-3 py-1.5 rounded-md bg-white/[0.03] border border-white/[0.06] text-[11px] font-mono text-gray-400 hover:bg-white/[0.05] transition-all"
        >
          取消
        </button>
      </div>
    </motion.div>
  );
}

// ============================================
// Bot 卡片
// ============================================

function BotCard({ bot, selected, onSelect, onRemove }: {
  bot: CozeBot;
  selected: boolean;
  onSelect: () => void;
  onRemove: () => void;
}) {
  return (
    <div
      onClick={onSelect}
      className={`group rounded-lg border p-2.5 cursor-pointer transition-all ${
        selected
          ? "border-blue-500/30 bg-blue-500/5"
          : "border-white/[0.05] bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/[0.08]"
      }`}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-1.5 flex-1 min-w-0">
          <div className="w-6 h-6 rounded-md bg-blue-500/15 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
            <Bot size={11} className="text-blue-400" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-medium text-white truncate">{bot.name}</div>
            <div className="text-[9px] font-mono text-gray-500 truncate">{bot.description}</div>
          </div>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          className="text-gray-600 hover:text-red-400 transition-colors flex-shrink-0 ml-1"
        >
          <Trash2 size={11} />
        </button>
      </div>
      <div className="flex items-center gap-2 mt-1.5 text-[9px] font-mono text-gray-600">
        <span>ID: {bot.bot_id.slice(0, 8)}...</span>
        <span>·</span>
        <span>调用 {bot.callCount} 次</span>
        {bot.lastUsedAt && (
          <>
            <span>·</span>
            <span>最近: {new Date(bot.lastUsedAt).toLocaleDateString()}</span>
          </>
        )}
      </div>
    </div>
  );
}
